import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  IconButton,
  Typography,
  Box,
  Tab,
  Tabs,
  Chip,
  Divider
} from '@mui/material';
import {
  Close as CloseIcon,
  Business as BusinessIcon,
  Payment as PaymentIcon,
  Category as CategoryIcon,
  Share as ShareIcon
} from '@mui/icons-material';
import { Button, TextField, Select, Autocomplete } from '../../design-system';
import useVendorStore from '../../stores/vendorStore';
import useUIStore from '../../stores/uiStore';
import {
  VENDOR_TYPES,
  PAYMENT_METHODS,
  VENDOR_CATEGORIES,
  ALL_CATEGORIES,
  SOCIAL_PLATFORMS,
  PAYMENT_TERMS
} from '../../constants/vendorConstants';

const VendorDialog = ({ open, onClose, onVendorCreated, initialName = '' }) => {
  const { quickCreateVendor, checkVendorName } = useVendorStore();
  const { addNotification } = useUIStore();
  
  // Reset form data when dialog opens
  useEffect(() => {
    if (open) {
      setFormData({
        name: initialName,
        type: 'supplier',
        contact: {
          email: '',
          phone: '',
          address: {
            street: '',
            city: '',
            state: '',
            postalCode: '',
            country: ''
          }
        },
        paymentInfo: {
          preferredMethod: 'bank_transfer',
          bankDetails: {
            bankName: '',
            accountName: '',
            accountNumber: '',
            iban: '',
            swiftCode: '',
            branchCode: ''
          },
          digitalPayment: {
            instapayNumber: '',
            paypalEmail: '',
            cryptoWallet: '',
            otherDetails: ''
          },
          paymentTermsDays: 30
        },
        categories: [],
        specializations: [],
        socialMedia: {
          instagram: { handle: '', followers: '', engagementRate: '' },
          tiktok: { handle: '', followers: '', engagementRate: '' },
          youtube: { handle: '', subscribers: '', avgViews: '' },
          twitter: { handle: '', followers: '' },
          facebook: { handle: '', followers: '' },
          linkedin: { handle: '', connections: '' }
        },
        description: '',
        notes: ''
      });
      setErrors({});
      setIsSubmitting(false);
      setNameExists(false);
      setCurrentTab(0);
    }
  }, [open, initialName]);
  
  const [formData, setFormData] = useState({
    name: initialName,
    type: 'supplier',
    contact: {
      email: '',
      phone: '',
      address: {
        street: '',
        city: '',
        state: '',
        postalCode: '',
        country: ''
      }
    },
    paymentInfo: {
      preferredMethod: 'bank_transfer',
      bankDetails: {
        bankName: '',
        accountName: '',
        accountNumber: '',
        iban: '',
        swiftCode: '',
        branchCode: ''
      },
      digitalPayment: {
        instapayNumber: '',
        paypalEmail: '',
        cryptoWallet: '',
        otherDetails: ''
      },
      paymentTermsDays: 30
    },
    categories: [],
    specializations: [],
    socialMedia: {
      instagram: { handle: '', followers: '', engagementRate: '' },
      tiktok: { handle: '', followers: '', engagementRate: '' },
      youtube: { handle: '', subscribers: '', avgViews: '' },
      twitter: { handle: '', followers: '' },
      facebook: { handle: '', followers: '' },
      linkedin: { handle: '', connections: '' }
    },
    description: '',
    notes: ''
  });
  
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [nameExists, setNameExists] = useState(false);
  const [currentTab, setCurrentTab] = useState(0);
  const [categoryOptions, setCategoryOptions] = useState(ALL_CATEGORIES);

  // Update category options based on vendor type
  useEffect(() => {
    let options = [];
    if (formData.type === 'influencer') {
      options = VENDOR_CATEGORIES.influencer;
    } else if (['service_provider', 'agency', 'consultant'].includes(formData.type)) {
      options = VENDOR_CATEGORIES.service;
    } else {
      options = ALL_CATEGORIES;
    }
    setCategoryOptions(options);
  }, [formData.type]);

  const handleChange = (field, value) => {
    // Debug categories value
    if (field === 'categories') {
      console.log('Categories value:', value);
      console.log('Categories type:', typeof value);
      if (Array.isArray(value)) {
        console.log('Categories array items:', value.map(v => ({type: typeof v, value: v})));
      }
    }
    
    // Handle nested fields
    if (field.includes('.')) {
      const keys = field.split('.');
      setFormData(prev => {
        const newData = { ...prev };
        let current = newData;
        
        for (let i = 0; i < keys.length - 1; i++) {
          if (!current[keys[i]]) current[keys[i]] = {};
          current[keys[i]] = { ...current[keys[i]] };
          current = current[keys[i]];
        }
        
        current[keys[keys.length - 1]] = value;
        return newData;
      });
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
    
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
    
    // Check if name exists
    if (field === 'name' && value) {
      checkVendorName(value).then(result => {
        console.log('checkVendorName result:', result);
        if (result && typeof result.exists === 'boolean') {
          setNameExists(result.exists);
        } else {
          console.error('Invalid result from checkVendorName:', result);
          setNameExists(false);
        }
      }).catch(err => {
        console.error('Error checking vendor name:', err);
        setNameExists(false);
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name) {
      newErrors.name = 'Vendor name is required';
    } else if (nameExists) {
      newErrors.name = 'A vendor with this name already exists';
    }
    
    // Validate email if provided
    if (formData.contact.email && !/^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/.test(formData.contact.email)) {
      newErrors['contact.email'] = 'Invalid email format';
    }
    
    // Validate phone if provided
    if (formData.contact.phone && !/^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/.test(formData.contact.phone)) {
      newErrors['contact.phone'] = 'Invalid phone format';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Only send fields with values
      const dataToSend = {
        name: formData.name,
        type: formData.type,
        description: formData.description || undefined,
        notes: formData.notes || undefined
      };
      
      // Add categories if any (extract values from objects)
      if (formData.categories.length > 0) {
        dataToSend.categories = formData.categories.map(cat => 
          typeof cat === 'string' ? cat : cat.value
        );
      }
      
      // Add specializations if any
      if (formData.specializations.length > 0) {
        dataToSend.specializations = formData.specializations;
      }
      
      // Only include contact if any contact field has a value
      const hasContactInfo = formData.contact.email || formData.contact.phone || 
                           formData.contact.address.street || formData.contact.address.city;
      
      if (hasContactInfo) {
        dataToSend.contact = {};
        if (formData.contact.email) dataToSend.contact.email = formData.contact.email;
        if (formData.contact.phone) dataToSend.contact.phone = formData.contact.phone;
        
        const hasAddress = formData.contact.address.street || formData.contact.address.city ||
                          formData.contact.address.state || formData.contact.address.postalCode ||
                          formData.contact.address.country;
        
        if (hasAddress) {
          dataToSend.contact.address = {};
          if (formData.contact.address.street) dataToSend.contact.address.street = formData.contact.address.street;
          if (formData.contact.address.city) dataToSend.contact.address.city = formData.contact.address.city;
          if (formData.contact.address.state) dataToSend.contact.address.state = formData.contact.address.state;
          if (formData.contact.address.postalCode) dataToSend.contact.address.postalCode = formData.contact.address.postalCode;
          if (formData.contact.address.country) dataToSend.contact.address.country = formData.contact.address.country;
        }
      }
      
      // Add payment info
      dataToSend.paymentInfo = {
        preferredMethod: formData.paymentInfo.preferredMethod,
        paymentTermsDays: formData.paymentInfo.paymentTermsDays
      };
      
      // Add payment details based on method
      if (formData.paymentInfo.preferredMethod === 'bank_transfer') {
        const hasBankDetails = Object.values(formData.paymentInfo.bankDetails).some(v => v);
        if (hasBankDetails) {
          dataToSend.paymentInfo.bankDetails = formData.paymentInfo.bankDetails;
        }
      } else {
        const hasDigitalPayment = Object.values(formData.paymentInfo.digitalPayment).some(v => v);
        if (hasDigitalPayment) {
          dataToSend.paymentInfo.digitalPayment = formData.paymentInfo.digitalPayment;
        }
      }
      
      // Add social media for influencers
      if (formData.type === 'influencer') {
        const socialData = {};
        for (const [platform, data] of Object.entries(formData.socialMedia)) {
          if (data.handle) {
            socialData[platform] = {
              handle: data.handle,
              ...(data.followers && { followers: parseInt(data.followers) }),
              ...(data.engagementRate && { engagementRate: parseFloat(data.engagementRate) }),
              ...(data.subscribers && { subscribers: parseInt(data.subscribers) }),
              ...(data.avgViews && { avgViews: parseInt(data.avgViews) }),
              ...(data.connections && { connections: parseInt(data.connections) })
            };
          }
        }
        if (Object.keys(socialData).length > 0) {
          dataToSend.socialMedia = socialData;
        }
      }
      
      const vendor = await quickCreateVendor(dataToSend);
      console.log('Created vendor:', vendor);
      
      addNotification({
        message: 'Vendor created successfully',
        type: 'success'
      });
      
      if (onVendorCreated && vendor) {
        onVendorCreated(vendor);
      }
      
      onClose();
    } catch (error) {
      addNotification({
        message: error.message || 'Failed to create vendor',
        type: 'error'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <BusinessIcon color="primary" />
            <Typography variant="h6">Create New Vendor</Typography>
          </Box>
          <IconButton onClick={handleClose} disabled={isSubmitting}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={currentTab} onChange={(e, value) => setCurrentTab(value)}>
          <Tab label="Basic Info" icon={<BusinessIcon />} iconPosition="start" />
          <Tab label="Payment" icon={<PaymentIcon />} iconPosition="start" />
          <Tab label="Categories" icon={<CategoryIcon />} iconPosition="start" />
          {formData.type === 'influencer' && (
            <Tab label="Social Media" icon={<ShareIcon />} iconPosition="start" />
          )}
        </Tabs>
      </Box>
      
      <DialogContent dividers>
        {/* Basic Information Tab */}
        {currentTab === 0 && (
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Vendor Name"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                error={!!errors.name}
                helperText={errors.name}
                required
                autoFocus
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Select
                label="Vendor Type"
                value={formData.type}
                onChange={(value) => handleChange('type', value)}
                options={VENDOR_TYPES}
                required
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                label="Description"
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                multiline
                rows={2}
                placeholder="Brief description of vendor services"
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                label="Notes"
                value={formData.notes}
                onChange={(e) => handleChange('notes', e.target.value)}
                multiline
                rows={3}
                placeholder="Additional notes about this vendor"
              />
            </Grid>
            
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }}>
                <Typography variant="subtitle2" color="textSecondary">
                  Contact Information
                </Typography>
              </Divider>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                label="Email"
                value={formData.contact.email}
                onChange={(e) => handleChange('contact.email', e.target.value)}
                type="email"
                error={!!errors['contact.email']}
                helperText={errors['contact.email']}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                label="Phone"
                value={formData.contact.phone}
                onChange={(e) => handleChange('contact.phone', e.target.value)}
                type="tel"
                error={!!errors['contact.phone']}
                helperText={errors['contact.phone']}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                label="Street Address"
                value={formData.contact.address.street}
                onChange={(e) => handleChange('contact.address.street', e.target.value)}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                label="City"
                value={formData.contact.address.city}
                onChange={(e) => handleChange('contact.address.city', e.target.value)}
              />
            </Grid>
            
            <Grid item xs={12} md={3}>
              <TextField
                label="State/Province"
                value={formData.contact.address.state}
                onChange={(e) => handleChange('contact.address.state', e.target.value)}
              />
            </Grid>
            
            <Grid item xs={12} md={3}>
              <TextField
                label="Postal Code"
                value={formData.contact.address.postalCode}
                onChange={(e) => handleChange('contact.address.postalCode', e.target.value)}
              />
            </Grid>
          </Grid>
        )}
        
        {/* Payment Tab */}
        {currentTab === 1 && (
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Select
                label="Preferred Payment Method"
                value={formData.paymentInfo.preferredMethod}
                onChange={(value) => handleChange('paymentInfo.preferredMethod', value)}
                options={PAYMENT_METHODS}
                required
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Select
                label="Payment Terms"
                value={formData.paymentInfo.paymentTermsDays}
                onChange={(value) => handleChange('paymentInfo.paymentTermsDays', value)}
                options={PAYMENT_TERMS}
              />
            </Grid>
            
            {formData.paymentInfo.preferredMethod === 'bank_transfer' && (
              <>
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Bank Details
                    </Typography>
                  </Divider>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Bank Name"
                    value={formData.paymentInfo.bankDetails.bankName}
                    onChange={(e) => handleChange('paymentInfo.bankDetails.bankName', e.target.value)}
                  />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Account Name"
                    value={formData.paymentInfo.bankDetails.accountName}
                    onChange={(e) => handleChange('paymentInfo.bankDetails.accountName', e.target.value)}
                  />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Account Number"
                    value={formData.paymentInfo.bankDetails.accountNumber}
                    onChange={(e) => handleChange('paymentInfo.bankDetails.accountNumber', e.target.value)}
                  />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <TextField
                    label="IBAN"
                    value={formData.paymentInfo.bankDetails.iban}
                    onChange={(e) => handleChange('paymentInfo.bankDetails.iban', e.target.value)}
                  />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <TextField
                    label="SWIFT Code"
                    value={formData.paymentInfo.bankDetails.swiftCode}
                    onChange={(e) => handleChange('paymentInfo.bankDetails.swiftCode', e.target.value)}
                  />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Branch Code"
                    value={formData.paymentInfo.bankDetails.branchCode}
                    onChange={(e) => handleChange('paymentInfo.bankDetails.branchCode', e.target.value)}
                  />
                </Grid>
              </>
            )}
            
            {['instapay', 'paypal', 'cryptocurrency'].includes(formData.paymentInfo.preferredMethod) && (
              <>
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Digital Payment Details
                    </Typography>
                  </Divider>
                </Grid>
                
                {formData.paymentInfo.preferredMethod === 'instapay' && (
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="InstaPay Number"
                      value={formData.paymentInfo.digitalPayment.instapayNumber}
                      onChange={(e) => handleChange('paymentInfo.digitalPayment.instapayNumber', e.target.value)}
                    />
                  </Grid>
                )}
                
                {formData.paymentInfo.preferredMethod === 'paypal' && (
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="PayPal Email"
                      value={formData.paymentInfo.digitalPayment.paypalEmail}
                      onChange={(e) => handleChange('paymentInfo.digitalPayment.paypalEmail', e.target.value)}
                      type="email"
                    />
                  </Grid>
                )}
                
                {formData.paymentInfo.preferredMethod === 'cryptocurrency' && (
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Crypto Wallet Address"
                      value={formData.paymentInfo.digitalPayment.cryptoWallet}
                      onChange={(e) => handleChange('paymentInfo.digitalPayment.cryptoWallet', e.target.value)}
                    />
                  </Grid>
                )}
              </>
            )}
          </Grid>
        )}
        
        {/* Categories Tab */}
        {currentTab === 2 && (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Autocomplete
                label="Categories"
                value={formData.categories}
                onChange={(value) => handleChange('categories', value)}
                options={categoryOptions}
                multiple
                getOptionLabel={(option) => {
                  console.log('getOptionLabel called with:', option);
                  return option.label || option;
                }}
                isOptionEqualToValue={(option, value) => {
                  if (typeof value === 'string') {
                    return option.value === value;
                  }
                  return option.value === value.value;
                }}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      variant="outlined"
                      label={typeof option === 'object' ? option.label : option}
                      {...getTagProps({ index })}
                      key={index}
                    />
                  ))
                }
              />
            </Grid>
            
            <Grid item xs={12}>
              <Autocomplete
                label="Specializations"
                value={formData.specializations}
                onChange={(value) => handleChange('specializations', value)}
                options={[]}
                multiple
                freeSolo
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      variant="outlined"
                      label={typeof option === 'object' ? option.label : option}
                      {...getTagProps({ index })}
                      key={index}
                    />
                  ))
                }
                helperText="Type and press Enter to add specializations"
              />
            </Grid>
          </Grid>
        )}
        
        {/* Social Media Tab (only for influencers) */}
        {currentTab === 3 && formData.type === 'influencer' && (
          <Grid container spacing={3}>
            {SOCIAL_PLATFORMS.map((platform) => (
              <Grid item xs={12} key={platform.value}>
                <Divider sx={{ my: 2 }}>
                  <Typography variant="subtitle2" color="textSecondary">
                    {platform.icon} {platform.label}
                  </Typography>
                </Divider>
                
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <TextField
                      label={`${platform.label} Handle`}
                      value={formData.socialMedia[platform.value].handle}
                      onChange={(e) => handleChange(`socialMedia.${platform.value}.handle`, e.target.value)}
                      placeholder={`@username`}
                    />
                  </Grid>
                  
                  {platform.value !== 'linkedin' ? (
                    <Grid item xs={12} md={4}>
                      <TextField
                        label={platform.value === 'youtube' ? 'Subscribers' : 'Followers'}
                        value={formData.socialMedia[platform.value].followers || formData.socialMedia[platform.value].subscribers}
                        onChange={(e) => handleChange(`socialMedia.${platform.value}.${platform.value === 'youtube' ? 'subscribers' : 'followers'}`, e.target.value)}
                        type="number"
                      />
                    </Grid>
                  ) : (
                    <Grid item xs={12} md={4}>
                      <TextField
                        label="Connections"
                        value={formData.socialMedia[platform.value].connections}
                        onChange={(e) => handleChange(`socialMedia.${platform.value}.connections`, e.target.value)}
                        type="number"
                      />
                    </Grid>
                  )}
                  
                  {['instagram', 'tiktok'].includes(platform.value) && (
                    <Grid item xs={12} md={4}>
                      <TextField
                        label="Engagement Rate %"
                        value={formData.socialMedia[platform.value].engagementRate}
                        onChange={(e) => handleChange(`socialMedia.${platform.value}.engagementRate`, e.target.value)}
                        type="number"
                      />
                    </Grid>
                  )}
                  
                  {platform.value === 'youtube' && (
                    <Grid item xs={12} md={4}>
                      <TextField
                        label="Average Views"
                        value={formData.socialMedia[platform.value].avgViews}
                        onChange={(e) => handleChange(`socialMedia.${platform.value}.avgViews`, e.target.value)}
                        type="number"
                      />
                    </Grid>
                  )}
                </Grid>
              </Grid>
            ))}
          </Grid>
        )}
      </DialogContent>
      
      <DialogActions>
        <Button
          variant="outlined"
          onClick={handleClose}
          disabled={isSubmitting}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          loading={isSubmitting}
        >
          Create Vendor
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default VendorDialog;